import { useHistory, useLocation } from "react-router-dom";
import { Flex, Text } from "@jsq/ds/core";
import {
  FormRadioGroup,
  FormRadioButton,
  FormSelect,
  FormTextInput,
  FormTextarea,
  CancelButton,
  SubmitButton,
  useFormContext,
} from "@jsq/ds/forms";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import CountryFormField from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/AddressFields/CountryFormField";
import RiskRatingRadioBar from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/RiskRatingRadioBar";
import { shouldShowRiskRating } from "~/pages/Compliance/utils";
import { AccountFormProps, PaymentInstructionFormValues } from "../types";
import {
  accountFormSelector,
  accountHolderExplanation,
  createNewProfileText,
  optionContainer,
  optionText,
  editIcon,
} from "./styles_vanilla.css";

export const AccountForm = ({
  profilesMap,
  programId,
  showBankAndCountry,
  onClose,
}: AccountFormProps) => {
  const { values } = useFormContext<PaymentInstructionFormValues>();
  const history = useHistory();
  const location = useLocation();

  const handleCreateNewProfile = () => {
    history.push(`${location.pathname}/profile`);
  };

  const handleEditNewProfile = (profileId: number) => {
    history.push(`${location.pathname}/profile-edit/${profileId}`);
  };

  return (
    <Flex gap={20} flexDirection="column">
      <FormSelect
        name="accountHolderId"
        label="Account holder"
        className={accountFormSelector}
        full
        data-testid="accountHolderId"
        placeholder="Select profile"
        defaultValue={values.accountHolderId}
        handleChange={(value: string | number | string[] | number[]) => {
          if (value === 0) {
            handleCreateNewProfile();
          }
        }}
      >
        {Array.from(profilesMap.entries())
          .filter(([_, options]) => options && options.length > 0)
          .map(([key, options]) => (
            <FormSelect.Section key={key} name={key}>
              {options.map((option) => (
                <FormSelect.Option key={`${option.name}`} value={option.id}>
                  {key === "New Profile" ? (
                    option.id === 0 ? (
                      <Text className={createNewProfileText}>{option.name}</Text>
                    ) : (
                      <Flex gap={Sizes.LG} alignItems="center" className={optionContainer}>
                        <Text className={optionText}>{option.name}</Text>
                        <Icon
                          type="edit"
                          data-testid="edit-profile-icon"
                          color={Colors.Primary600}
                          className={editIcon}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditNewProfile(option.id);
                          }}
                        />
                      </Flex>
                    )
                  ) : (
                    option.name
                  )}
                </FormSelect.Option>
              ))}
            </FormSelect.Section>
          ))}
      </FormSelect>
      <Flex>
        <Text variant="strong-md">
          Please make sure to include this bank account owner in watchlist screening. If the
          screening returns a positive result, create a profile for the bank account owner and
          perform KYC on them.
        </Text>
      </Flex>
      {showBankAndCountry && (
        <Flex gap={20}>
          <FormTextInput name="bankName" label="Bank name" isFullWidth />
          <CountryFormField />
        </Flex>
      )}
      <FormRadioGroup name="accountNameMatches">
        <FormRadioButton value="yes">
          Account (investor, owner, or controller) exactly matches bank account name
        </FormRadioButton>
        <FormRadioButton value="no">
          Account (investor, owner, or controller) does not exactly match bank account name
        </FormRadioButton>
      </FormRadioGroup>
      {values.accountNameMatches === "no" && (
        <Flex flexDirection="column" gap={20}>
          <FormTextInput name="bankAccountName" label="Bank account name" isFullWidth />
          <FormTextarea
            name="explanation"
            label="Explanation"
            className={accountHolderExplanation}
            placeholder="Please explain how this bank account owner is related to the investor"
            isFullWidth
          />
        </Flex>
      )}
      {shouldShowRiskRating(programId) && <RiskRatingRadioBar form="bankAccount" />}
      <Flex gap={20} justifyContent="flex-end">
        <CancelButton onClick={onClose} />
        <SubmitButton>Save</SubmitButton>
      </Flex>
    </Flex>
  );
};
